// because of https://docs.slatejs.org/concepts/12-typescript#multiple-document-models
// @ts-nocheck
/* eslint-disable */

import React, { FC, useState, useEffect } from 'react';
import { Editor, Transforms } from 'slate';
import { RenderPlaceholderProps } from 'slate-react';
import { ClickAwayListener, makeStyles, FormControl } from '@material-ui/core';
import { getIn, useField, useFormikContext } from 'formik';
import classNames from 'classnames';

import { MentionsInstance } from 'components';
import { MentionElement } from './MentionsInstance';
import { Paragraph } from '../types';
interface Props {
  fieldName: string;
  mentionsFieldName: string;
  placeholder: string;
  renderPlaceholder?: (props: RenderPlaceholderProps) => JSX.Element;
  disabled?: boolean;
  editor: Editor;
}

const useStyles = makeStyles(theme => ({
  editor: {
    paddingBottom: 0,
  },
  focused: {
    '&:after': {
      border: `2px solid ${theme.palette.primary.main}`,
      borderRadius: 4,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      content: '""',
      position: 'absolute',
      transform: 'scaleX(1)',
      pointerEvents: 'none',
    },
  },
  label: {
    marginTop: -theme.spacing(2),
  },
  error: {
    border: `1px solid ${theme.palette.error.main}`,
  },
}));

export type SlateMentionsValue = MentionElement | Paragraph;

const blankSlateValue = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

const MentionsEditor: FC<Props> = props => {
  const classes = useStyles(props);
  const { fieldName, placeholder, renderPlaceholder, disabled, mentionsFieldName, editor } = props;
  const { errors, touched, setFieldValue } = useFormikContext();

  const [slateValue, setSlateValue] = useState<SlateMentionsValue[]>(blankSlateValue);
  const [focused, setFocused] = useState(false);
  const [field] = useField(fieldName);

  const hasError = !!getIn(touched, fieldName) && !!getIn(errors, fieldName);
  const errorText = getIn(errors, fieldName);
  const editorClasses = classNames(classes.editor, {
    [classes.error]: hasError,
    [classes.focused]: focused,
  });

  function handleClickAway() {
    setFocused(false);
  }

  useEffect(() => {
    if (slateValue === blankSlateValue && !!field.value) {
      Transforms.delete(editor, {
        at: {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, []),
        },
      });

      Transforms.removeNodes(editor, {
        at: [0],
      });
      Transforms.insertNodes(editor, [
        {
          type: 'paragraph',
          children: [{ text: field.value }],
        },
      ]);
    }
  }, [field.value]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <FormControl error={hasError} fullWidth focused={focused}>
        <div className={editorClasses}>
          <MentionsInstance
            editor={editor}
            slateValue={slateValue}
            fieldName={fieldName}
            mentionsFieldName={mentionsFieldName}
            placeholder={placeholder}
            renderPlaceholder={renderPlaceholder}
            onChange={setSlateValue}
            onFocus={() => setFocused(true)}
            disabled={disabled}
          />
        </div>
        {hasError && <FormHelperText>{errorText}</FormHelperText>}
      </FormControl>
    </ClickAwayListener>
  );
};

export default MentionsEditor;

export function withMentions(editor: Editor) {
  const { isInline, isVoid } = editor;

  editor.isInline = element => {
    return element.type === 'mention' ? true : isInline(element);
  };

  editor.isVoid = element => {
    return element.type === 'mention' ? true : isVoid(element);
  };

  return editor;
}
